import React from 'react';
import { Box, Paper, Skeleton, ToggleButton, Typography, styled } from '@mui/material';
import 'chart.js/auto';

import { Row } from 'ui/Flex';
import { LineChart } from 'ui/analytics/LineChart';
import { ReviewsTimeGranularity, useReviewsLineChartData } from '../hooks/useReviewsLineChartData';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { useReviewTrendsQueries } from '../hooks/useReivewTrendsQueries';
import { StyledToggleButtonGroup } from './StyledToggleButtonGroup';

const ChartSkeleton = styled(Skeleton)(({ theme }) => ({
    width: '100%',
    height: '100%',
    borderRadius: theme.shape.borderRadius
}));

interface ReviewsLineChartProps {
    dateRange: DateRangePickerValue;
    selectedLocationIds: string[];
}

export function ReviewsLineChart({ dateRange, selectedLocationIds }: ReviewsLineChartProps) {
    const [granularity, setGranularity] = React.useState<ReviewsTimeGranularity>('month');
    const { volumeData } = useReviewTrendsQueries(dateRange, selectedLocationIds, granularity);

    const handleGranularityChange = React.useCallback(
        (_: React.MouseEvent, value: ReviewsTimeGranularity | null) => {
            if (value) {
                setGranularity(value);
            }
        },
        []
    );

    const { chartData, chartOptions } = useReviewsLineChartData({ volumeData, granularity });

    return (
        <Paper variant="outlined" sx={{ py: 3, px: 2, mt: 3 }} elevation={0}>
            <Row align="space-between" valign="flex-start">
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Review Volume Trends
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Number of reviews collected over time by rating
                    </Typography>
                </Box>
                <StyledToggleButtonGroup
                    value={granularity}
                    exclusive
                    onChange={handleGranularityChange}
                    size="small"
                >
                    <ToggleButton value="month">Monthly</ToggleButton>
                    <ToggleButton value="week">Weekly</ToggleButton>
                    <ToggleButton value="day">Daily</ToggleButton>
                </StyledToggleButtonGroup>
            </Row>
            <Box mt={2}>
                {volumeData?.loading ? (
                    <Box height={400} position="relative">
                        <ChartSkeleton variant="rectangular" />
                    </Box>
                ) : (
                    <LineChart chartData={chartData} chartOptions={chartOptions} height={400} />
                )}
            </Box>
        </Paper>
    );
}
